import { action, comparer, computed, observable, reaction, toJS } from 'mobx'
import { object, identifier, serializable } from 'serializr'
import ItemStore from '../ItemStore'
import FilterStore from '../FilterStore'
import HitList from '../../api/HitList'

import { typeFromId, getDcCounts } from '../../utils/FlyBaseIds'

// Import a class to track the DataClass Filter state.
import DataClassFilter from '../DataClassFilter'
import SpeciesFilter from '../SpeciesFilter'
import Query from '../Query'

import {
  LIST_VIEW,
  TABLE_VIEW,
  DEFAULT_ITEM_PER_PAGE,
  DEFAULT_SPECIES,
} from '../../constants'

class ViewState {
  @serializable(identifier())
  key

  @serializable(object(ItemStore))
  @observable
  itemStore

  @observable
  filterStore

  @serializable
  @observable
  displayMode

  @observable
  itemsLoading

  @observable
  isSorting = false

  requestedPageIds

  sortRequest = 0

  @computed
  get isLoading() {
    return this.itemsLoading || this.isSorting || this.speciesFilter.isLoading
  }

  @observable
  error

  @serializable(object(Query))
  @observable
  query

  @serializable(object(DataClassFilter))
  @observable
  dcFilter

  @serializable(object(SpeciesFilter))
  @observable
  speciesFilter

  // The current page.
  //@serializable
  @observable
  currentPage

  // How many items we want to see per page.
  //@serializable
  @observable
  numItemsPerPage

  @observable
  sortName
  @observable
  sortOrder
  @observable
  sortedIds = []

  constructor(
    key = 'hitlist',
    itemStore = new ItemStore(),
    filterStore = new FilterStore(),
    query = new Query()
  ) {
    this.key = key

    this.setFilterStore(filterStore)
    this.setDcFilter()
    this.setSpeciesFilter()
    this.setItemStore(itemStore)
    this.setQuery(query)

    this.setIsLoading(true)
    this.setDisplayMode(LIST_VIEW)
    this.setCurrentPage(0)
    this.setNumItemsPerPage(DEFAULT_ITEM_PER_PAGE)

    /*

     These reactions take two arguments.  The first is a function that returns a variable
     or object to watch for changes.  The second argument is a function that is executed
     when the variable of object is changed.  If the property or object is modifed, but
     its previous state was the same, the function will not fire.  The return value from
     the first function can be used as an argument for the second function if need be.

     This is how important hitlist logic between different components and parts of the
     global store/state is implemented.

     These reactions are documented at https://mobx.js.org/refguide/reaction.html
     */

    // Update the Data Class Filter items used by the Data Class Filter component.
    // This fires automatically when the counts in the ItemStore are updated.
    reaction(
      () => [...this.itemStore.counts.entries()],
      () => this.dcFilter.setMenuItems(this.itemStore.counts)
    )

    // Load only the final visible page, after species metadata and sorting settle.
    // Compare actual IDs: filter bookkeeping can change without changing the page.
    reaction(
      () =>
        this.speciesFilter.hasLoaded &&
        !this.speciesFilter.isLoading &&
        !this.isSorting
          ? this.filteredIds.slice(this.begin, this.end)
          : null,
      (ids) => {
        if (ids !== null && !comparer.structural(ids, this.requestedPageIds)) {
          this.fetchPageItemsFromServer()
        }
      },
      {
        delay: 500,
        equals: comparer.structural,
      }
    )

    /*
    If the user changes the number of items per page, reset the page back to 1.
    The page-ID reaction above handles the resulting slice change.
    */
    reaction(
      () => this.numItemsPerPage,
      () => this.setCurrentPage(1)
    )

    // If we switch from table to list view, ensure that the items per page is
    // set back to the default.
    reaction(
      () => this.displayMode === LIST_VIEW,
      () => {
        this.setNumItemsPerPage(DEFAULT_ITEM_PER_PAGE)
      }
    )

    // Update the data class filter counts based on the result of the applied filters.
    // Take care to ignore the dcFilter.
    reaction(
      () => this.filteredIds,
      () => {
        let opts = {
          skip: 'dcFilter',
          speciesWanted: this.speciesFilter.selected,
          speciesFilter: this.speciesFilter,
        }
        const filteredIds = this.filterStore.applyFilters(
          this.itemStore.ids,
          opts
        )
        const dcCounts = getDcCounts(filteredIds)
        for (const dc of this.dcFilter.menuItems.keys()) {
          const count = dcCounts[dc] ? dcCounts[dc] : 0
          this.dcFilter.setCount(dc, count)
        }
      },
      { delay: 500 }
    )

    // This reaction watches the selected data classes.
    // If changed and the current list is a mixed hitlist in table view mode, switch
    // it back to list view.
    reaction(
      () => this.dcFilter.selectedItems,
      (selectedItems) => {
        // Recalculate the species counts
        let opts = {
          skip: 'speciesFilter',
          dataClasses: selectedItems,
          speciesWanted: this.speciesFilter.selected,
          speciesFilter: this.speciesFilter,
        }
        const filteredIds = this.filterStore.applyFilters(
          this.itemStore.ids,
          opts
        )
        this.speciesFilter.calculateCounts(filteredIds)

        if (selectedItems !== 1 && this.displayMode === TABLE_VIEW) {
          this.setDisplayMode(LIST_VIEW)
          this.resetSort()
        }
      },
      { delay: 500 }
    )

    // Reset the sort order if the user changes the enabled filters.
    reaction(
      () => this.filterStore.enabledFilterCount,
      () => this.resetSort()
    )

    // TODO: This should be optimized to reverse current sorted order if we already have it.
    reaction(
      () => {
        return { name: this.sortName, order: this.sortOrder }
      },
      () => {
        this.fetchSortOrder()
      }
    )

    // Fetch species information when new IDs are loaded.
    reaction(
      () => this.itemStore.ids,
      () => this.speciesFilter.loadSpecies(this.itemStore.ids)
    )

    // Refresh the default selected species when the filter data is updated.
    reaction(
      () => this.filterStore.data,
      (filterData) => {
        const speciesData = toJS(filterData.get('species')) || DEFAULT_SPECIES
        this.speciesFilter.setSelected(speciesData.selected)
      }
    )

    this.initFilters()
  }

  @action
  setItemStore = (store) => (this.itemStore = store)
  @action
  setFilterStore = (store) => (this.filterStore = store)
  @action
  setQuery = (query) => (this.query = query)

  @action
  setDisplayMode(mode = LIST_VIEW) {
    this.displayMode = mode
  }

  @action
  setIsLoading(status = false) {
    this.itemsLoading = status
  }

  @action
  setError(error) {
    this.error = error
  }

  @action
  setSort = (name = 'id', order = 'asc') => {
    this.sortName = name
    this.sortOrder = order
  }
  @action
  setSortedIds = (ids = []) => (this.sortedIds = ids)

  @action
  resetSort = () => {
    this.setSort(null, null)
    this.setSortedIds([])
  }

  @action
  fetchSortOrder() {
    const request = ++this.sortRequest
    // Resetting sorting already restores the original IDs; no server work needed.
    if (this.sortName === null && this.sortOrder === null) {
      this.isSorting = false
      return
    }
    const api = new HitList()

    this.isSorting = true
    api
      .sortItems(this.filteredIds, this.sortName, this.sortOrder)
      .then(action((json) => {
        if (request !== this.sortRequest) return
        if (json.resultset.result[0] && json.resultset.result[0].ids) {
          this.setSortedIds(json.resultset.result[0].ids)
        } else {
          throw new Error('Sort failed to fetch info from server.')
        }
        this.isSorting = false
        const pageIds = this.filteredIds.slice(this.begin, this.end)
        if (!comparer.structural(pageIds, this.requestedPageIds)) {
          this.fetchPageItemsFromServer()
        }
      }))
      .catch(action((err) => {
        if (request !== this.sortRequest) return
        this.isSorting = false
        this.setError(err)
      }))
  }

  @action
  setCurrentPage = (page = 1) => (this.currentPage = page)

  @action
  setNumItemsPerPage = (numPerPage = DEFAULT_ITEM_PER_PAGE) =>
    (this.numItemsPerPage = numPerPage)

  @action
  setDcFilter = (filter = new DataClassFilter()) => (this.dcFilter = filter)
  @action
  setSpeciesFilter = (filter = new SpeciesFilter()) =>
    (this.speciesFilter = filter)

  @computed
  get numPages() {
    return Math.ceil(this.filteredIds.length / this.numItemsPerPage)
  }
  @computed
  get begin() {
    let begin = 0
    if (this.currentPage > 1) {
      begin = (this.currentPage - 1) * this.numItemsPerPage
    }
    return begin
  }

  @computed
  get end() {
    let end = this.begin + this.numItemsPerPage
    if (end > this.filteredIds.length) {
      end = this.filteredIds.length
    }
    return end
  }

  @action
  fetchPageItemsFromServer() {
    if (!this.speciesFilter.hasLoaded || this.speciesFilter.isLoading || this.isSorting) return
    this.setError(undefined)
    this.itemStore.setItems([])

    // Get filtered list of IDs to work with.
    const ids = this.filteredIds

    const itemIds = ids.slice(this.begin, this.end)
    this.requestedPageIds = itemIds.slice()
    // TODO: Avoid loading records more than we have to from the server.
    // Get ids that we don't already have items for.
    //const itemsToFetch = difference(itemIds,Object.keys(this.itemStore.items));
    const onSuccess = (json) => {
      this.setIsLoading(false)
    }
    const onFail = (err) => {
      this.requestedPageIds = undefined
      this.setIsLoading(false)
      this.setError(err)
    }

    this.setIsLoading(true)
    this.itemStore.loadItems(itemIds, onSuccess, onFail)
  }

  @computed
  get filteredIds() {
    let opts = {
      dataClasses: this.dcFilter.selectedItems,
      speciesWanted: this.speciesFilter.selected,
      speciesFilter: this.speciesFilter,
    }

    if (this.sortedIds.length !== 0) {
      return this.filterStore.applyFilters(this.sortedIds, opts)
    }
    return this.filterStore.applyFilters(this.itemStore.ids, opts)
  }

  @computed
  get filteredSelectedIds() {
    let opts = {
      dataClasses: this.dcFilter.selectedItems,
      speciesWanted: this.speciesFilter.selected,
      speciesFilter: this.speciesFilter,
    }
    /*    if (this.filterStore.data.has('species')) {
      opts.species = this.filterStore.data.get('species').slice()
    }*/
    return this.filterStore.applyFilters(this.itemStore.selectedIds, opts)
  }

  @action
  selectAllFiltered() {
    this.itemStore.selectAll(this.filteredIds)
  }

  @action
  deSelectAllFiltered() {
    this.itemStore.deSelectAll(this.filteredIds)
  }

  /*
   Computed value that determines if all available filtered IDs are selected or not.
   Returns the following values.  This operates filtered IDs only.

   'none' - No IDs have been selected.
   'all'  - All IDs have been selected.
   'some' - Some IDs have been selected.
   */
  @computed
  get selectedMode() {
    const selectedCount = this.filteredSelectedIds.length
    const filterIdCount = this.filteredIds.length
    let mode = 'none'

    if (selectedCount === filterIdCount) {
      mode = 'all'
    } else if (selectedCount > 0) {
      mode = 'some'
    }
    return mode
  }

  initFilters() {
    this.filterStore.addFilter('dcFilter', (ids, opts) =>
      DataClassFilter.filterIdsByDataClass(ids, opts)
    )

    this.filterStore.addFilter('speciesFilter', (ids, opts) =>
      SpeciesFilter.filterIdsBySpecies(ids, opts)
    )
  }

  // Returns true or false depending on if the filtered IDs are of one type or not.
  @computed
  get isUniform() {
    return this.dataClasses.length === 1 ? true : false
  }

  @computed
  get dataClasses() {
    const ids = this.filteredIds
    const types = {}

    for (const id of ids) {
      const type = typeFromId(id)
      types[type] = null
    }
    return Object.keys(types)
  }
}
export default ViewState
